import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Paper, Button, Typography, TextField,
  Alert, CircularProgress, Divider, List, ListItem, ListItemIcon, ListItemText,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import LinkIcon from '@mui/icons-material/Link';
import { supabase } from '../../supabaseClient';
import { useAuth } from '../../components/AuthProvider';
import logo from '../../components/logo_black.png';

type Step = 'loading' | 'login' | 'magic_sent' | 'confirm' | 'connecting' | 'done' | 'error';

interface SellerProfile {
  id: string;
  store_name: string;
}

export default function Share2dmConnect() {
  // URL 파라미터
  const searchParams = new URLSearchParams(window.location.search);
  const brandId = searchParams.get('brand_id');
  const state = searchParams.get('state') ?? '';
  const callback = searchParams.get('callback') ?? '';
  const originParam = searchParams.get('origin') ?? '';
  const origin = originParam ? decodeURIComponent(originParam) : 'https://share2dm.xyz';

  const { user, session, loading } = useAuth();

  const [step, setStep] = useState<Step>('loading');
  const [sellerProfile, setSellerProfile] = useState<SellerProfile | null>(null);
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // seller_profile 조회
  const loadSellerProfile = useCallback(async (userId: string) => {
    const { data, error } = await supabase
      .from('seller_profile')
      .select('id, store_name')
      .eq('user_id', userId)
      .single();

    if (error || !data) {
      setErrorMsg('셀러 프로필을 찾을 수 없습니다. 셀러로 등록된 계정으로 로그인해주세요.');
      setStep('error');
      return;
    }
    setSellerProfile(data);
    setStep('confirm');
  }, []);

  // 인증 상태 체크: session 바뀔 때마다 실행
  useEffect(() => {
    if (loading) return;
    if (!session || !user) {
      // magic_sent 상태에서 대기 중인 경우 유지
      setStep(prev => prev === 'magic_sent' ? 'magic_sent' : 'login');
      return;
    }
    // 로그인 완료 → seller_profile 불러오기
    loadSellerProfile(user.id);
  }, [loading, session, user, loadSellerProfile]);

  // 매직 링크 발송
  const handleSendMagicLink = async (e: React.FormEvent) => {
    e.preventDefault();
    setSending(true);
    setErrorMsg(null);

    // 등록된 이메일인지 확인
    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('id')
      .eq('email', email)
      .single();

    if (profileError || !profile) {
      setErrorMsg('등록되지 않은 이메일입니다. 셀러 계정으로 가입된 이메일을 입력해주세요.');
      setSending(false);
      return;
    }

    // 현재 페이지 URL 전체를 emailRedirectTo로 사용 → 클릭 후 이 페이지로 돌아옴
    const currentPageUrl = `${window.location.origin}/auth/share2dm-connect?${searchParams.toString()}`;

    const { error: otpError } = await supabase.auth.signInWithOtp({
      email,
      options: { emailRedirectTo: currentPageUrl },
    });

    if (otpError) {
      setErrorMsg(otpError.message);
    } else {
      setStep('magic_sent');
    }
    setSending(false);
  };

  // 연결 허용: RPC로 토큰 생성 → share2dm Worker callback redirect
  const handleConfirm = async () => {
    if (!sellerProfile || !brandId || !callback) return;
    setStep('connecting');

    const { data: token, error: rpcError } = await supabase
      .rpc('create_share2dm_connect_token', { p_brand_id: brandId });

    if (rpcError || !token) {
      console.error('[Share2dm Connect] token create error:', rpcError?.message);
      setErrorMsg('연결 토큰 생성에 실패했습니다. 다시 시도해주세요.');
      setStep('error');
      return;
    }

    // share2dm Worker callback URL로 리다이렉트
    const callbackParams = new URLSearchParams({
      token: token as string,
      brand_id: brandId,
      state,
      origin: encodeURIComponent(origin),
    });

    window.location.href = `${callback}?${callbackParams}`;
  };

  // 다른 계정으로 연결 → 로그아웃 후 로그인 화면
  const handleSwitchAccount = async () => {
    await supabase.auth.signOut();
    setSellerProfile(null);
    setStep('login');
  };

  // 연결 취소 → share2dm 대시보드로
  const handleCancel = () => {
    window.location.href = `${origin}/dashboard`;
  };

  // 필수 파라미터 없음
  if (!brandId || !callback) {
    return (
      <CenteredLayout>
        <Typography variant="h6" fontWeight={700} mb={1}>잘못된 접근입니다</Typography>
        <Typography color="text.secondary" fontSize={14}>
          필수 연결 파라미터가 없습니다. share2dm 대시보드에서 다시 시도해주세요.
        </Typography>
      </CenteredLayout>
    );
  }

  // ── STEP: loading ──
  if (step === 'loading') {
    return (
      <CenteredLayout>
        <CircularProgress size={32} sx={{ color: '#111' }} />
        <Typography mt={2} color="text.secondary" fontSize={14}>인증 확인 중...</Typography>
      </CenteredLayout>
    );
  }

  // ── STEP: login / magic_sent ──
  if (step === 'login' || step === 'magic_sent') {
    return (
      <OuterBox>
        <ConnectPaper>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
            <img src={logo} alt="Clozet" style={{ height: 32 }} />
            <LinkIcon sx={{ color: '#ccc', fontSize: 20 }} />
            <Typography fontWeight={700} fontSize={16}>share2dm</Typography>
          </Box>

          <Typography variant="h6" fontWeight={700} mb={0.5}>share2dm 연결 요청</Typography>
          <Typography color="text.secondary" fontSize={14} mb={3}>
            share2dm이 Clozet 콘텐츠에 연결을 요청합니다.<br />
            계속하려면 셀러 계정으로 로그인해주세요.
          </Typography>

          <Divider sx={{ mb: 3 }} />

          {step === 'magic_sent' ? (
            <Alert severity="success" sx={{ mb: 2 }}>
              로그인 링크를 이메일로 전송했습니다.<br />
              이메일을 확인하고 링크를 클릭하면 자동으로 진행됩니다.
            </Alert>
          ) : (
            <Box component="form" onSubmit={handleSendMagicLink}>
              <TextField
                label="이메일"
                placeholder="셀러 계정 이메일"
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                fullWidth
                required
                size="small"
                sx={{ mb: 2 }}
              />
              {errorMsg && <Alert severity="error" sx={{ mb: 2 }}>{errorMsg}</Alert>}
              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={sending}
                sx={{ py: 1.2, fontWeight: 700, bgcolor: '#111', '&:hover': { bgcolor: '#333' } }}
              >
                {sending ? '전송 중...' : '로그인 링크 받기'}
              </Button>
              <Typography fontSize={12} color="text.secondary" mt={1.5} textAlign="center">
                ⚠️ 이메일은 현재 기기에서 확인해주세요
              </Typography>
            </Box>
          )}
        </ConnectPaper>
      </OuterBox>
    );
  }

  // ── STEP: confirm ──
  if (step === 'confirm' && sellerProfile) {
    return (
      <OuterBox>
        <ConnectPaper>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
            <img src={logo} alt="Clozet" style={{ height: 32 }} />
            <LinkIcon sx={{ color: '#ccc', fontSize: 20 }} />
            <Typography fontWeight={700} fontSize={16}>share2dm</Typography>
          </Box>

          <Typography variant="h6" fontWeight={700} mb={0.5}>연결 권한 확인</Typography>
          <Typography color="text.secondary" fontSize={14} mb={2}>
            <strong>share2dm</strong>이 아래 스토어의 콘텐츠에 접근하려 합니다.
          </Typography>

          {/* 스토어 정보 */}
          <Box sx={{ bgcolor: '#f5f5f5', borderRadius: 2, px: 2.5, py: 1.5, mb: 3 }}>
            <Typography fontSize={13} color="text.secondary" mb={0.5}>연결할 스토어</Typography>
            <Typography fontWeight={700} fontSize={18}>{sellerProfile.store_name}</Typography>
          </Box>

          <Divider sx={{ mb: 2 }} />

          {/* 권한 목록 */}
          <Typography fontSize={13} fontWeight={600} mb={1}>share2dm에 부여되는 권한</Typography>
          <List dense disablePadding sx={{ mb: 3 }}>
            {[
              'Clozet 릴스 콘텐츠 short_code 조회',
              '콘텐츠 제품 정보 조회',
              '릴스 URL과 share2dm 캠페인 자동 연결',
            ].map(item => (
              <ListItem key={item} disableGutters sx={{ py: 0.3 }}>
                <ListItemIcon sx={{ minWidth: 28 }}>
                  <CheckCircleOutlineIcon sx={{ fontSize: 16, color: '#16a34a' }} />
                </ListItemIcon>
                <ListItemText primary={item} primaryTypographyProps={{ fontSize: 13 }} />
              </ListItem>
            ))}
          </List>

          {errorMsg && <Alert severity="error" sx={{ mb: 2 }}>{errorMsg}</Alert>}

          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <Button
              variant="contained"
              fullWidth
              onClick={handleConfirm}
              sx={{ py: 1.2, fontWeight: 700, bgcolor: '#111', '&:hover': { bgcolor: '#333' } }}
            >
              연결 허용
            </Button>
            <Button
              variant="outlined"
              fullWidth
              onClick={handleCancel}
              sx={{ py: 1.2, fontWeight: 600 }}
            >
              취소
            </Button>
          </Box>

          <Typography fontSize={11} color="text.secondary" mt={2} textAlign="center">
            연결 후 share2dm 대시보드에서 언제든지 해제할 수 있습니다.
          </Typography>

          <Divider sx={{ my: 2 }} />

          <Button
            fullWidth
            size="small"
            onClick={handleSwitchAccount}
            sx={{ color: '#888', fontSize: 12, textTransform: 'none' }}
          >
            다른 계정으로 연결하기
          </Button>
        </ConnectPaper>
      </OuterBox>
    );
  }

  // ── STEP: connecting ──
  if (step === 'connecting') {
    return (
      <CenteredLayout>
        <CircularProgress size={32} sx={{ color: '#111' }} />
        <Typography mt={2} fontWeight={600}>연결 처리 중...</Typography>
        <Typography mt={1} color="text.secondary" fontSize={13}>
          share2dm으로 이동합니다.
        </Typography>
      </CenteredLayout>
    );
  }

  // ── STEP: error ──
  return (
    <CenteredLayout>
      <Alert severity="error" sx={{ mb: 2, maxWidth: 400 }}>{errorMsg ?? '알 수 없는 오류가 발생했습니다.'}</Alert>
      <Button variant="outlined" onClick={handleCancel}>share2dm으로 돌아가기</Button>
    </CenteredLayout>
  );
}

// ── Layout helpers ──
function OuterBox({ children }: { children: React.ReactNode }) {
  return (
    <Box sx={{
      width: '100vw', minHeight: '100vh',
      bgcolor: '#f5f5f5',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      p: 2,
    }}>
      {children}
    </Box>
  );
}

function ConnectPaper({ children }: { children: React.ReactNode }) {
  return (
    <Paper elevation={0} sx={{
      width: '100%', maxWidth: 440,
      p: { xs: 3, sm: 4 },
      borderRadius: 3,
      boxShadow: '0 2px 20px rgba(0,0,0,0.08)',
    }}>
      {children}
    </Paper>
  );
}

function CenteredLayout({ children }: { children: React.ReactNode }) {
  return (
    <OuterBox>
      <Box sx={{ textAlign: 'center' }}>{children}</Box>
    </OuterBox>
  );
}
